/**
 * Tiny Markdown subset for policy pages: #/##/### headings, paragraphs, - and 1. lists,
 * > quotes, **bold**, *italic*, [links](url). Produces a structure the apps
 * render as React elements, so page text can never inject HTML.
 */
export type Inline = { t: 'text' | 'bold' | 'italic'; v: string } | { t: 'link'; v: string; href: string };
export type Block =
  | { t: 'h'; level: 1 | 2 | 3; parts: Inline[] }
  | { t: 'p'; parts: Inline[] }
  | { t: 'ul'; items: Inline[][] }
  | { t: 'ol'; items: Inline[][] }
  | { t: 'quote'; parts: Inline[] };

const SAFE_HREF = /^(https?:\/\/|\/|mailto:)/i;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push({ t: 'text', v: text.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ t: 'bold', v: m[1] });
    else if (m[2] !== undefined) out.push({ t: 'italic', v: m[2] });
    else if (m[3] !== undefined && m[4] !== undefined) {
      out.push(SAFE_HREF.test(m[4]) ? { t: 'link', v: m[3], href: m[4] } : { t: 'text', v: m[3] });
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ t: 'text', v: text.slice(last) });
  return out;
}

export function parseMarkdown(src: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let ordered = false;
  const flush = () => {
    if (para.length) blocks.push({ t: 'p', parts: parseInline(para.join(' ')) });
    if (list.length) blocks.push({ t: ordered ? 'ol' : 'ul', items: list.map(parseInline) });
    para = [];
    list = [];
  };
  for (const raw of src.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim();
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (!line) flush();
    else if (h) {
      flush();
      blocks.push({ t: 'h', level: h[1]!.length as 1 | 2 | 3, parts: parseInline(h[2]!) });
    } else if (/^([-*]|\d{1,3}\.)\s+/.test(line)) {
      const isOrdered = /^\d/.test(line);
      if (para.length || (list.length && isOrdered !== ordered)) flush();
      ordered = isOrdered;
      list.push(line.replace(/^([-*]|\d{1,3}\.)\s+/, ''));
    } else if (line.startsWith('>')) {
      flush();
      blocks.push({ t: 'quote', parts: parseInline(line.replace(/^>\s?/, '')) });
    } else {
      if (list.length) flush();
      para.push(line);
    }
  }
  flush();
  return blocks;
}
